import { ActivityDetail } from '@/types/activitydetail';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Calendar, Users, Clock, MapPin } from 'lucide-react';
import { BookingCalendar } from '@/components/booking-page/BookingCalendar';
import { fetchActivityDetails } from '@/utils/endUserAppApi';
import { toast } from '@/hooks/use-toast';

const PriceDisplay = ({ activity }: { activity: ActivityDetail }) => {
  const { t } = useTranslation();
  
  if (!activity.price) {
    return <span>{t('common.contactUs')}</span>;
  }

  return activity.price.isFree ? (
    <span>{t('common.free')}</span>
  ) : (
    <span>
      {activity.price.currency && activity.price.amount ? 
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: activity.price.currency
        }).format(activity.price.amount) :
        `$${activity.price.amount || 0}`
      }
    </span>
  );
};

export function BookingPage() {
  const { t } = useTranslation();
  const { id: activityId } = useParams(); // Fix: route param is 'id', not 'activityId'
  const [activity, setActivity] = useState<ActivityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadActivity = async () => {
      try {
        if (!activityId) throw new Error('No activity ID provided');
        const data = await fetchActivityDetails(activityId);
        setActivity(data);
      } catch (error) {
        console.error('Error loading activity:', error);
        toast({
          title: t('booking.error.title'),
          description: t('booking.error.description'),
          variant: "destructive",
        });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadActivity();
  }, [activityId, navigate, t]);

  if (loading || !activity) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded mb-4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                size="sm"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> {t('back')}
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{activity.name}</h1>
                <p className="text-sm text-gray-500">{activity.activityType || 'Activity'}</p>
              </div>
            </div>
            <div className="text-right">
              <PriceDisplay activity={activity} />
            </div>
          </div>
        </div>
      </div>

      {/* Activity Hero Image */}
      {activity.images && activity.images.length > 0 && (
        <div className="relative h-48 sm:h-64 lg:h-80">
          <img
            src={activity.images[0]}
            alt={activity.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/50"></div>
          <div className="absolute bottom-4 left-4 text-white">
            <h2 className="text-2xl sm:text-3xl font-bold drop-shadow-lg">{activity.name}</h2>
            <p className="text-sm sm:text-base opacity-90 drop-shadow">{activity.description}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          
          {/* Main Booking Flow - Takes 8 columns on desktop */}
          <div className="lg:col-span-8">
            <div className="space-y-4">
              
              {/* Combined Header with Progress */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold">{t('booking.calendar.title')}</h2>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">1</div>
                        <span className="text-xs font-medium text-blue-600 hidden sm:inline">{t('booking.steps.selectDateTime')}</span>
                      </div>
                      <div className="w-4 h-0.5 bg-gray-200"></div>
                      <div className="flex items-center space-x-1">
                        <div className="w-6 h-6 bg-gray-200 text-gray-400 rounded-full flex items-center justify-center text-xs font-medium">2</div>
                        <span className="text-xs text-gray-400 hidden sm:inline">{t('booking.steps.confirmDetails')}</span>
                      </div>
                      <div className="w-4 h-0.5 bg-gray-200"></div>
                      <div className="flex items-center space-x-1">
                        <div className="w-6 h-6 bg-gray-200 text-gray-400 rounded-full flex items-center justify-center text-xs font-medium">3</div>
                        <span className="text-xs text-gray-400 hidden sm:inline">{t('booking.steps.payment')}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Main Booking Calendar - More compact */}
              <Card>
                <CardContent className="p-4">
                  <BookingCalendar 
                    activityId={activityId!}
                    activity={activity}
                  />
                </CardContent>
              </Card>

              {/* Booking Instructions - Simplified */}
              <div className="text-center py-2">
                <p className="text-sm text-gray-600 mb-1">
                  {t('booking.noFees')} • {t('booking.bestPrice')}
                </p>
              </div>
            </div>
          </div>

          {/* Activity Info Sidebar - Takes 4 columns on desktop */}
          <div className="lg:col-span-4">
            <div className="sticky top-20 space-y-4">
              
              {/* Activity Overview */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-base font-semibold mb-3">Activity Details</h3>
                  <p className="text-gray-600 text-sm mb-3">{activity.description}</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center text-sm">
                      <Clock className="h-4 w-4 text-gray-400 mr-3" />
                      <span className="text-gray-600">
                        {typeof activity.duration === 'object' && activity.duration?.value 
                          ? `${activity.duration.value} ${activity.duration.unit}` 
                          : activity.duration ? `${activity.duration} minutes` : 'Duration varies'}
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      <Users className="h-4 w-4 text-gray-400 mr-3" />
                      <span className="text-gray-600">
                        Up to {activity.maxGroupSize || activity.maxParticipants || '20'} participants
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      <MapPin className="h-4 w-4 text-gray-400 mr-3" />
                      <span className="text-gray-600">
                        {activity.location?.address?.name || 
                         activity.location?.address?.city || 
                         activity.location?.name || 
                         `${activity.location?.address?.city || 'Location'}, ${activity.location?.address?.country || ''}`}
                      </span>
                    </div>
                    
                    {/* Price Display */}
                    <div className="flex items-center text-sm">
                      <div className="h-4 w-4 text-gray-400 mr-3 flex items-center justify-center">
                        <span className="text-xs">💰</span>
                      </div>
                      <span className="text-gray-600 font-medium">
                        <PriceDisplay activity={activity} /> per person
                      </span>
                    </div>
                    
                    {/* Activity Type */}
                    {activity.activityType && (
                      <div className="flex items-center text-sm">
                        <div className="h-4 w-4 text-gray-400 mr-3 flex items-center justify-center">
                          <span className="text-xs">🏷️</span>
                        </div>
                        <span className="text-gray-600 capitalize">
                          {activity.activityType.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* What to Expect */}
              {activity.animals && activity.animals.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-base font-semibold mb-3">Wildlife You'll See</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {(Array.isArray(activity.animals) ? activity.animals : []).slice(0, 6).map((animal, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          {typeof animal === 'object' && animal.emoji ? (
                            <span className="text-lg">{animal.emoji}</span>
                          ) : (
                            <span className="text-lg">🐾</span>
                          )}
                          <span className="text-gray-600 capitalize">
                            {typeof animal === 'object' ? animal.name : animal}
                          </span>
                        </div>
                      ))}
                    </div>
                    {Array.isArray(activity.animals) && activity.animals.length > 6 && (
                      <p className="text-xs text-gray-500 mt-2">
                        +{activity.animals.length - 6} more species
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Booking Guarantees */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-base font-semibold mb-3">Booking Benefits</h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Instant confirmation</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>No hidden fees</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Free cancellation</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Best price guarantee</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}